import { useState, useEffect } from 'react'
import { Copy, Check, RefreshCw, Plug, Shield, Terminal, Wrench } from 'lucide-react'
import { mcpApi } from '../services/api'
import { getApiBaseUrl } from '../utils/config'
import {
  PageHeader,
  LoadingSpinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '../components/common'

const McpSettings = () => {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generatedToken, setGeneratedToken] = useState('')
  const [copiedField, setCopiedField] = useState(null)

  const baseUrl = getApiBaseUrl()
  const mcpEndpoint = `${baseUrl}/mcp`

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      setLoading(true)
      const response = await mcpApi.getStatus()
      setStatus(response.data)
    } catch (error) {
      console.error('Error loading MCP status:', error)
      setStatus({ enabled: false, tokenConfigured: false, endpoint: '/mcp' })
    } finally {
      setLoading(false)
    }
  }

  const generateToken = () => {
    const bytes = new Uint8Array(20)
    crypto.getRandomValues(bytes)
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
    setGeneratedToken(`mcp_${hex}`)
  }

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      // fallback
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }

  const claudeCodeConfig = JSON.stringify({
    mcpServers: {
      reziland: {
        type: 'sse',
        url: mcpEndpoint,
        headers: {
          Authorization: `Bearer ${generatedToken || '<your-token>'}`
        }
      }
    }
  }, null, 2)

  const tools = [
    { name: 'list_tasks', description: 'List support tasks with filters (status, priority, project scope)', category: 'Tasks' },
    { name: 'get_task', description: 'Get detailed task info including comments and attachments', category: 'Tasks' },
    { name: 'update_task_status', description: 'Change task status (Open, InProgress, OnHold, Resolved, Closed, Backlog)', category: 'Tasks' },
    { name: 'add_task_comment', description: 'Add a comment to a task', category: 'Tasks' },
    { name: 'list_sentry_issues', description: 'List Sentry issues with filters (project, environment, status)', category: 'Sentry' },
    { name: 'get_sentry_issue', description: 'Get Sentry issue details with stack trace and breadcrumbs', category: 'Sentry' },
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MCP Settings" subtitle="Model Context Protocol server configuration" />
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="MCP Settings"
        subtitle="Model Context Protocol server configuration for AI assistants"
        actions={
          <Button variant="secondary" onClick={loadStatus}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        }
      />

      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Plug className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold">Server Status</h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-500">Status</span>
              <Badge variant={status?.enabled ? 'success' : 'danger'}>
                {status?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-500">Token</span>
              <Badge variant={status?.tokenConfigured ? 'success' : 'warning'}>
                {status?.tokenConfigured ? 'Configured' : 'Not Configured'}
              </Badge>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-500">Endpoint</span>
              <code className="text-sm text-gray-700 bg-white px-2 py-1 rounded border">{mcpEndpoint}</code>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Token Generator Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold">Token Generator</h3>
          </div>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-gray-600 mb-4">
            Generate a secure API token for MCP authentication. Copy the token and add it to your server's
            <code className="mx-1 px-1 bg-gray-100 rounded text-xs">appsettings.json</code>
            under <code className="mx-1 px-1 bg-gray-100 rounded text-xs">McpServer.ApiToken</code>,
            then use the same token in your Claude Code configuration below.
          </p>

          <div className="flex items-center gap-3 mb-4">
            <Button onClick={generateToken}>
              Generate Token
            </Button>
          </div>

          {generatedToken && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800 mb-2">
                Save this token now — it won't be shown again after you leave this page.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-white px-3 py-2 rounded border border-amber-300 font-mono break-all">
                  {generatedToken}
                </code>
                <button
                  onClick={() => copyToClipboard(generatedToken, 'token')}
                  className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  title="Copy token"
                >
                  {copiedField === 'token' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Connection Instructions Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold">Connection Instructions</h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">1. Add to Claude Code settings</h4>
              <p className="text-sm text-gray-600 mb-2">
                Add the following to your <code className="px-1 bg-gray-100 rounded text-xs">~/.claude/settings.json</code> or
                project <code className="px-1 bg-gray-100 rounded text-xs">.claude/settings.json</code>:
              </p>
              <div className="relative">
                <pre className="text-sm bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto font-mono">
                  {claudeCodeConfig}
                </pre>
                <button
                  onClick={() => copyToClipboard(claudeCodeConfig, 'config')}
                  className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                  title="Copy config"
                >
                  {copiedField === 'config' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">2. Configure the server</h4>
              <p className="text-sm text-gray-600">
                Ensure <code className="px-1 bg-gray-100 rounded text-xs">McpServer.Enabled</code> is <code className="px-1 bg-gray-100 rounded text-xs">true</code> and
                <code className="px-1 bg-gray-100 rounded text-xs">McpServer.ApiToken</code> matches the token you generated above
                in the server's <code className="px-1 bg-gray-100 rounded text-xs">appsettings.json</code>.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">3. Verify connection</h4>
              <p className="text-sm text-gray-600">
                Restart Claude Code and verify the MCP tools are available. You should see 6 tools from the "reziland" server.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Available Tools Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold">Available Tools</h3>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell header>Tool</TableCell>
                <TableCell header>Category</TableCell>
                <TableCell header>Description</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tools.map((tool) => (
                <TableRow key={tool.name}>
                  <TableCell>
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{tool.name}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant={tool.category === 'Tasks' ? 'primary' : 'danger'}>
                      {tool.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">{tool.description}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  )
}

export default McpSettings
